import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  IconButton,
  Button,
  TextField,
  Tabs,
  Tab,
  InputAdornment,
} from '@mui/material';
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  Add as AddIcon,
  Search as SearchIcon,
  RestoreFromTrash as RestoreIcon,
  Clear as ClearIcon,
  UploadFile as UploadFileIcon,
  FilterListOff as FilterListOffIcon,
} from '@mui/icons-material';
import { Article } from '../types';
import { deleteArticle, articlesAPI } from '../services/api';
import ArticleForm from './ArticleForm';
import ArticleBulkImportDialog from './ArticleBulkImportDialog';
import AlertDialog from './AlertDialog';
import useAlert from '../hooks/useAlert';
import { getModuleTableTheme } from '../theme/tableTheme';
import { extractErrorMessage } from '../utils/errorHandler';
import CustomTablePagination from './CustomTablePagination';
import PermissionButton from './PermissionButton';
import ColumnFilter from './ColumnFilter';
import { useColumnFilters } from '../hooks/useColumnFilters';
import { useArticlesList, useDeletedArticles, useInvalidateArticles } from '../hooks/queries/useArticles';

const CONFIG_COLOR = '#1976D2';

const ARTICLE_FILTER_SCHEMA = {
  reference:     { type: 'text', param: 'reference' },
  designation:   { type: 'text', param: 'designation' },
  category:      { type: 'text', param: 'category' },
  unit:          { type: 'text', param: 'unit' },
  seuil_alerte:  { type: 'numberRange', paramMin: 'seuil_min', paramMax: 'seuil_max' },
  date_creation: { type: 'dateRange', paramFrom: 'date_from', paramTo: 'date_to' },
} as const;

const ArticleList: React.FC = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [selectedArticle, setSelectedArticle] = useState<Article | null>(null);
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [isBulkImportOpen, setIsBulkImportOpen] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [tabValue, setTabValue] = useState(0);
  const { alertState, showError, showSuccess, showConfirm, hideAlert } = useAlert();
  const { filters, update, reset, hasActive, toQueryParams } = useColumnFilters(ARTICLE_FILTER_SCHEMA, {
    onChange: () => setPage(0),
  });

  // Debounce search term
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchTerm);
      setPage(0); // Reset to first page on search
    }, 300);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  const listParams = useMemo(() => {
    const extra = toQueryParams();
    return {
      skip: page * rowsPerPage,
      limit: rowsPerPage,
      search: debouncedSearch || undefined,
      extra: Object.keys(extra).length > 0 ? extra : undefined,
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, rowsPerPage, debouncedSearch, filters]);

  const articlesQuery = useArticlesList(listParams, tabValue === 0);
  const deletedQuery = useDeletedArticles(tabValue === 1);
  const invalidate = useInvalidateArticles();

  const articles = articlesQuery.data?.items ?? [];
  const totalCount = articlesQuery.data?.total ?? 0;
  const deletedArticles = deletedQuery.data ?? [];
  const loading = tabValue === 0 ? articlesQuery.isInitialLoading : deletedQuery.isInitialLoading;

  const handleRestoreArticle = async (article: Article) => {
    try {
      await articlesAPI.restore(article.id);
      showSuccess('Article restauré avec succès');
      invalidate();
    } catch (err: any) {
      showError(extractErrorMessage(err));
      console.error('Error restoring article:', err);
    }
  };

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
    setPage(0);
  };

  const handleDeleteArticle = async (article: Article) => {
    try {
      await deleteArticle(article.id);
      showSuccess('Article supprimé avec succès');
      invalidate();
    } catch (err: any) {
      showError(extractErrorMessage(err));
      console.error('Error deleting article:', err);
    }
  };

  const handleFormSuccess = () => {
    setIsFormOpen(false);
    setSelectedArticle(null);
    invalidate();
  };

  const handleClearSearch = () => {
    setSearchTerm('');
  };

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // For deleted tab, use client-side pagination
  const deletedPage = tabValue === 1 ? page : 0;
  const paginatedDeletedArticles = deletedArticles.slice(
    deletedPage * rowsPerPage,
    deletedPage * rowsPerPage + rowsPerPage
  );

  return (
    <Box p={3} sx={{ width: '100%', maxWidth: 'none' }}>
      <Typography variant="h4" gutterBottom>
        Gestion des Articles
      </Typography>

      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
        <Tabs value={tabValue} onChange={handleTabChange}>
          <Tab label={`Articles Actifs (${totalCount})`} />
          <Tab label="Corbeille" />
        </Tabs>
      </Box>

      {tabValue === 0 && (
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Box display="flex" gap={2}>
          <TextField
            variant="outlined"
            placeholder="Rechercher par référence ou désignation..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            size="small"
            sx={{ minWidth: 350 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
              endAdornment: searchTerm && (
                <InputAdornment position="end">
                  <IconButton size="small" onClick={handleClearSearch}>
                    <ClearIcon fontSize="small" />
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
        </Box>

        <Box display="flex" gap={1} alignItems="center">
          {hasActive() && (
            <Button
              size="small"
              startIcon={<FilterListOffIcon />}
              onClick={reset}
              sx={{ color: 'text.secondary' }}
            >
              Effacer les filtres
            </Button>
          )}
          <PermissionButton
            moduleName="Config"
            operation="add"
            miniModuleName="Articles"
            variant="outlined"
            onClick={() => setIsBulkImportOpen(true)}
            startIcon={<UploadFileIcon />}
            sx={{
              borderColor: '#1976D2',
              color: '#1976D2',
              '&:hover': {
                borderColor: '#1565C0',
                backgroundColor: 'rgba(25, 118, 210, 0.04)',
              },
            }}
          >
            Importer Excel
          </PermissionButton>
          <PermissionButton
            moduleName="Config"
            operation="add"
            miniModuleName="Articles"
            variant="contained"
            onClick={() => setIsFormOpen(true)}
            startIcon={<AddIcon />}
            sx={{
              backgroundColor: '#1976D2',
              '&:hover': {
                backgroundColor: '#1565C0',
              }
            }}
          >
            Nouveau Article
          </PermissionButton>
        </Box>
      </Box>
      )}

      {tabValue === 0 && (
      <>
      <TableContainer component={Paper} sx={{ ...getModuleTableTheme('configuration'), width: '100%', maxWidth: 'none' }}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  Référence
                  <ColumnFilter type="text" value={filters.reference} onChange={(v) => update('reference', v)} label="référence" color={CONFIG_COLOR} />
                </Box>
              </TableCell>
              <TableCell>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  Désignation
                  <ColumnFilter type="text" value={filters.designation} onChange={(v) => update('designation', v)} label="désignation" color={CONFIG_COLOR} />
                </Box>
              </TableCell>
              <TableCell>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  Catégorie
                  <ColumnFilter type="text" value={filters.category} onChange={(v) => update('category', v)} label="catégorie" color={CONFIG_COLOR} />
                </Box>
              </TableCell>
              <TableCell>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  Unité
                  <ColumnFilter type="text" value={filters.unit} onChange={(v) => update('unit', v)} label="unité" color={CONFIG_COLOR} />
                </Box>
              </TableCell>
              <TableCell align="right">
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end' }}>
                  Seuil d'Alerte
                  <ColumnFilter type="numberRange" value={filters.seuil_alerte} onChange={(v) => update('seuil_alerte', v)} label="seuil d'alerte" color={CONFIG_COLOR} />
                </Box>
              </TableCell>
              <TableCell>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  Date Création
                  <ColumnFilter type="dateRange" value={filters.date_creation} onChange={(v) => update('date_creation', v)} label="date de création" color={CONFIG_COLOR} />
                </Box>
              </TableCell>
              <TableCell align="center">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={7} align="center">
                  Chargement...
                </TableCell>
              </TableRow>
            ) : articles.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} align="center">
                  {debouncedSearch || hasActive() ? 'Aucun article trouvé pour cette recherche' : 'Aucun article trouvé'}
                </TableCell>
              </TableRow>
            ) : (
              articles.map((article) => (
                <TableRow key={article.id}>
                  <TableCell>{article.reference}</TableCell>
                  <TableCell>{article.designation}</TableCell>
                  <TableCell>{article.category_name}</TableCell>
                  <TableCell>{article.unit_name}</TableCell>
                  <TableCell align="right">{article.seuil_alerte}</TableCell>
                  <TableCell>{article.date_creation_formatted}</TableCell>
                  <TableCell align="center">
                    <Box display="flex" justifyContent="center" alignItems="center" gap={0.5}>
                      <PermissionButton
                        moduleName="Config"
                        operation="edit"
                        miniModuleName="Articles"
                        component="IconButton"
                        onClick={() => {
                          setSelectedArticle(article);
                          setIsFormOpen(true);
                        }}
                        size="small"
                      >
                        <EditIcon />
                      </PermissionButton>
                      <PermissionButton
                        moduleName="Config"
                        operation="delete"
                        miniModuleName="Articles"
                        component="IconButton"
                        onClick={() => {
                          showConfirm(
                            `Êtes-vous sûr de vouloir supprimer l'article "${article.designation}" ?`,
                            () => handleDeleteArticle(article),
                            {
                              title: 'Confirmer la suppression',
                              confirmText: 'Supprimer',
                              cancelText: 'Annuler'
                            }
                          );
                        }}
                        color="error"
                        size="small"
                      >
                        <DeleteIcon />
                      </PermissionButton>
                    </Box>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
      <CustomTablePagination
        count={totalCount}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
      />
      </>
      )}

      {tabValue === 1 && (
      <>
      <TableContainer component={Paper} sx={{ ...getModuleTableTheme('configuration'), width: '100%', maxWidth: 'none' }}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Référence</TableCell>
              <TableCell>Désignation</TableCell>
              <TableCell>Catégorie</TableCell>
              <TableCell>Unité</TableCell>
              <TableCell align="right">Seuil d'Alerte</TableCell>
              <TableCell>Date Suppression</TableCell>
              <TableCell align="center">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={7} align="center">
                  Chargement...
                </TableCell>
              </TableRow>
            ) : deletedArticles.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} align="center">
                  Aucun article supprimé
                </TableCell>
              </TableRow>
            ) : (
              paginatedDeletedArticles.map((article) => (
                <TableRow key={article.id}>
                  <TableCell>{article.reference}</TableCell>
                  <TableCell>{article.designation}</TableCell>
                  <TableCell>{article.category_name}</TableCell>
                  <TableCell>{article.unit_name}</TableCell>
                  <TableCell align="right">{article.seuil_alerte}</TableCell>
                  <TableCell>{article.deleted_at ? new Date(article.deleted_at).toLocaleDateString('fr-FR') : '-'}</TableCell>
                  <TableCell align="center">
                    <IconButton
                      onClick={() => {
                        showConfirm(
                          `Êtes-vous sûr de vouloir restaurer l'article "${article.designation}" ?`,
                          () => handleRestoreArticle(article),
                          {
                            title: 'Confirmer la restauration',
                            confirmText: 'Restaurer',
                            cancelText: 'Annuler'
                          }
                        );
                      }}
                      color="primary"
                      size="small"
                      title="Restaurer"
                    >
                      <RestoreIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
      <CustomTablePagination
        count={deletedArticles.length}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
      />
      </>
      )}

      <ArticleForm
        open={isFormOpen}
        onClose={() => {
          setIsFormOpen(false);
          setSelectedArticle(null);
        }}
        onSuccess={handleFormSuccess}
        article={selectedArticle}
        onShowMessage={(type, message) => {
          if (type === 'success') {
            showSuccess(message);
          } else {
            showError(message);
          }
        }}
      />

      <ArticleBulkImportDialog
        open={isBulkImportOpen}
        onClose={() => setIsBulkImportOpen(false)}
        onSuccess={() => {
          invalidate();
        }}
        onShowMessage={(type, message) => {
          if (type === 'success') {
            showSuccess(message);
          } else {
            showError(message);
          }
        }}
      />

      <AlertDialog
        open={alertState.open}
        type={alertState.type}
        title={alertState.title}
        message={alertState.message}
        onClose={hideAlert}
        onConfirm={alertState.onConfirm}
        confirmText={alertState.confirmText}
        cancelText={alertState.cancelText}
        showCancel={alertState.showCancel}
      />
    </Box>
  );
};

export default ArticleList;
